// Fixture time helpers. Stored PWHL fixture times are free text in US Eastern
// wall-clock (e.g. "9:50 PM EST"). We interpret them as America/New_York, then
// render in the VIEWER'S local timezone (their location) with a 12h/24h option.

import { prefTzLabel } from "./timePrefs";

const SRC_ZONE = "America/New_York";

// Parse "9:50 PM EST" / "7:00 pm ET" / "19:00" -> { h, min } wall-clock (24h).
function parseTimeText(t) {
    if (!t) return null;
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = (m[3] || "").toLowerCase();
    if (ap.startsWith("p") && h < 12) h += 12;
    if (ap.startsWith("a") && h === 12) h = 0;
    if (h > 23 || min > 59) return null;
    return { h, min };
}

// Given a wall-clock time in `timeZone`, return the matching UTC instant (Date).
function wallToInstant(y, mo, d, h, mi, timeZone) {
    const guess = Date.UTC(y, mo - 1, d, h, mi);
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
    const parts = {};
    for (const p of dtf.formatToParts(new Date(guess))) parts[p.type] = p.value;
    let hh = +parts.hour;
    if (hh === 24) hh = 0;
    const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, hh, +parts.minute, +parts.second);
    return new Date(guess - (asUTC - guess));
}

// Read the viewer's preferred clock (12h vs 24h / military). Mirrored to
// localStorage by My Account so every page can read it without an auth call.
export function prefHour12() {
    try { return (localStorage.getItem("rosterly_time_format") || "12h") !== "24h"; }
    catch { return true; }
}

// Format a fixture's date + Eastern time text into the viewer's local time.
// Returns e.g. "2:50 AM GMT" (12h) or "02:50 GMT" (24h). Falls back to the raw
// text if it can't be parsed.
export function formatFixtureTime(dateISO, timeText, opts = {}) {
    const hour12 = opts.hour12 != null ? opts.hour12 : prefHour12();
    const parsed = parseTimeText(timeText);
    if (!dateISO || !parsed) return timeText || "TBD";
    const [y, mo, d] = String(dateISO).split("-").map(Number);
    if (!y || !mo || !d) return timeText || "TBD";
    let instant;
    try { instant = wallToInstant(y, mo, d, parsed.h, parsed.min, SRC_ZONE); }
    catch { return timeText || "TBD"; }
    if (isNaN(instant.getTime())) return timeText || "TBD";
    try {
        return new Intl.DateTimeFormat(undefined, {
            hour: "numeric", minute: "2-digit", hour12, ...(prefTzLabel() ? { timeZoneName: "short" } : {}),
        }).format(instant);
    } catch {
        return timeText || "TBD";
    }
}
